import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar, Toolbar, Button, IconButton, Badge,
  BottomNavigation, BottomNavigationAction, Box, Divider, List,
  ListItem, ListItemText, ListItemIcon, CircularProgress,
  Paper, ClickAwayListener, Popper, Typography, Switch,
  FormControlLabel, Snackbar, Alert
} from '@mui/material';
import {
  ShoppingCart, AccountCircle, Home, Wallet,
  AdminPanelSettings, Notifications as NotificationsIcon,
  NotificationsNone, DoneAll, Close, Logout
} from '@mui/icons-material';
import PhoneIcon from '@mui/icons-material/Phone';
import ShareIcon from '@mui/icons-material/Share';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useNotifications } from '../context/NotificationContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

const NavBar = () => {
  const { cartCount, setCartOpen } = useCart();
  const { user, logout } = useAuth();
  const {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    loading
  } = useNotifications();

  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [pharmacyStatus, setPharmacyStatus] = useState(false); // Default to close
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notificationRef = useRef(null);
  // Determine the current navigation value based on the URL path
  const getCurrentNavigationValue = () => {
    const path = location.pathname;
    
    if (path === '/' || path === '/home') return 'home';
    if (path === '/account') return 'account';
    if (path === '/wallet') return 'wallet';
    if (path === '/admin') return 'admin';
    if (path === '/about') return 'about';    
    return ''; // Return empty if not matching any navigation item
  };

  const [value, setValue] = useState(getCurrentNavigationValue());

  // Update the navigation value when the location changes
  useEffect(() => {
    setValue(getCurrentNavigationValue());
  }, [location.pathname]);

  // Load pharmacy status from server on component mount
  useEffect(() => {
    const fetchPharmacyStatus = async () => {
      try {
        const response = await api.get('/categories/pharm_status');
        setPharmacyStatus(response.data.value === '1');
      } catch (error) {
        console.error('Error fetching pharmacy status:', error);
        // Fallback to localStorage if API fails
        const savedStatus = localStorage.getItem('pharmacyStatus');
        if (savedStatus !== null) {
          setPharmacyStatus(savedStatus === 'true');
        }
      }
    };
    fetchPharmacyStatus();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotificationClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (event) => {
    const newStatus = event.target.checked;
    setLoadingStatus(true);
    
    try {
      // Update status on server
      const response = await api.put('/admin/pharm_status', {
        key: 'pharm_open_status',
        value: newStatus ? '1' : '0'
      });

      setPharmacyStatus(newStatus);
      localStorage.setItem('pharmacyStatus', newStatus);
      
      setSnackbar({
        open: true,
        message: newStatus ? 'الصيدلية مفتوحة الآن' : 'الصيدلية مغلقة الآن',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating pharmacy status:', error);
      setSnackbar({
        open: true,
        message: 'فشل في تحديث حالة الصيدلية',
        severity: 'error'
      });
      // Revert the change if API call fails
      setPharmacyStatus(!newStatus);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const [isCopied, setIsCopied] = useState(false);
  const currentUrl = window.location.href;
  const siteTitle = document.title;

  const shareWithWebAPI = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: siteTitle,
          text: `تابع أحدث العروض على ${siteTitle}`,
          url: currentUrl,
        });
        console.log('Shared successfully');
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      console.log('Web Share API not supported');
      copyToClipboard();
    }
  };

  // Copy URL to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(currentUrl).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popper' : undefined;

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          top: 0,
          backgroundColor: 'primary.main',
          boxShadow: 3,
          padding: '5px 0',
          zIndex: 1201
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          
          {/* Pharmacy Logo */}
         <Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    maxWidth: { xs: '180px', sm: '240px' }
  }}
  onClick={() => navigate('/')}
>
  <svg
    width="100%"
    height="auto"
    viewBox="0 0 450 120"
    xmlns="http://www.w3.org/2000/svg"
    style={{ borderRadius: '20px' }}
  >
    {/* Background with gradient */}
    <rect x="0" y="0" width="450" height="120" rx="25" fill="url(#gradient)" />
    
    {/* Gradient definition */}
    <defs>
      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#ba7002" />
        <stop offset="100%" stopColor="#ca8d30" />
      </linearGradient>
    </defs>
    
    {/* Logo/Brand Mark - Wholesale Box Icon */}
    <g transform="translate(40, 30)">
      {/* Box/Package symbol */}
      <rect x="0" y="10" width="40" height="30" rx="5" fill="#FF9800" stroke="#FFF" strokeWidth="2" />
      <rect x="5" y="0" width="30" height="15" rx="3" fill="#FF9800" stroke="#FFF" strokeWidth="2" />
      <line x1="20" y1="15" x2="20" y2="25" stroke="#FFF" strokeWidth="2" />
      <line x1="10" y1="20" x2="30" y2="20" stroke="#FFF" strokeWidth="2" />
      
      {/* Network/connection dots */}
      <circle cx="35" cy="5" r="3" fill="#4CAF50" />
      <circle cx="45" cy="15" r="3" fill="#4CAF50" />
      <circle cx="35" cy="25" r="3" fill="#4CAF50" />
    </g>
    
    {/* Text - Arabic branding for wholesale market */}
    <g fontFamily="Cairo, sans-serif" fontWeight="bold">
      {/* Main brand name */}
      <text x="300" y="50" fill="#FFFFFF" fontSize="32">
        جملة لاين
      </text>
      
      {/* Subtitle - Online */}
      <text x="300" y="85" fill="#B7E600" fontSize="24">
        الإلكتروني
      </text>
      
      {/* Tagline in smaller text */}
      <text x="300" y="110" fill="#E3F2FD" fontSize="16" fontWeight="500">
        تجارة الجملة أونلاين
      </text>
    </g>
    
    {/* Status indicator */}
    <g>
      <circle cx="410" cy="30" r="12" fill={pharmacyStatus ? '#4CAF50' : '#F44336'} stroke="white" strokeWidth="2" />
      <text x="410" y="35" textAnchor="middle" fill="white" fontSize="10" fontFamily="Arial">
        {pharmacyStatus ? 'ON' : 'OFF'}
      </text>
    </g>
    
    {/* Optional: Decorative elements */}
    <g opacity="0.1">
      <circle cx="420" cy="100" r="15" fill="#FFFFFF" />
      <circle cx="380" cy="90" r="10" fill="#FFFFFF" />
      <rect x="30" y="90" width="20" height="20" rx="5" fill="#FFFFFF" />
    </g>
  </svg>
</Box>
          {/* Right Side Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {user ? (
              <>
                {/* Pharmacy Status Toggle (Admin Only) */}
                {user.role === 'admin' && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {loadingStatus ? (
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                    ) : (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={pharmacyStatus}
                            onChange={handleStatusChange}
                            color="success"
                            disabled={loadingStatus}
                          />
                        }
                        label={
                          <Typography variant="body2" color="white">
                            {pharmacyStatus ? 'مفتوح' : 'مغلق'}
                          </Typography>
                        }
                        labelPlacement="start"
                        sx={{ mr: 1 }}
                      />
                    )}
                  </Box>
                )}
                
                <IconButton color="inherit" onClick={() => setCartOpen(true)}>
                  <Badge badgeContent={cartCount} color="secondary">
                    <ShoppingCart />
                  </Badge>
                </IconButton>
                <IconButton
                  color="inherit"
                  ref={notificationRef}
                  onClick={handleNotificationClick}
                >
                  <Badge badgeContent={unreadCount} color="secondary">
                    {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNone />}
                  </Badge>
                </IconButton>
                <Button
                  color="inherit"
                  onClick={handleLogout}
                >
                  <Logout />
                </Button>
                <Button
                  color="inherit"
                  onClick={shareWithWebAPI}
                >
                  <ShareIcon />
                </Button>
              </>
            ) : (
              <>
                <Button color="inherit" component={Link} to="/login">
                  تسجيل الدخول
                </Button>
                <Button color="inherit" component={Link} to="/register">
                  التسجيل
                </Button>
                <Button
                  color="inherit"
                  onClick={shareWithWebAPI}
                >
                  <ShareIcon sx={{ mr: 1 }} />
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Notifications Dropdown */}
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        sx={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            sx={{
              width: 350,
              maxHeight: 400,
              overflow: 'auto',
              boxShadow: 3,
            }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">الإشعارات</Typography>
              <Box>
                <IconButton size="small" onClick={markAllAsRead} disabled={unreadCount === 0}>
                  <DoneAll fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleClose}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Divider />

            {loading ? (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography>لا يوجد إشعارات</Typography>
              </Box>
            ) : (
              <List dense>
                {notifications.map((notification) => (
                  <ListItem
                    key={notification.id}
                    sx={{
                      bgcolor: notification.read ? 'background.paper' : 'action.selected',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => {
                      markAsRead(notification.id);
                      if (notification.link) {
                        navigate(notification.link);
                      }
                      handleClose();
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {notification.icon === 'order' && <ShoppingCart fontSize="small" />}
                      {notification.icon === 'system' && <AdminPanelSettings fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={notification.title}
                      secondary={notification.message}
                      primaryTypographyProps={{
                        fontWeight: notification.read ? 'normal' : 'bold',
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Bottom Navigation */}
      <BottomNavigation
        value={value}
        onChange={(event, newValue) => {
          setValue(newValue);
          navigate(`/${newValue === 'home' ? '' : newValue}`);
        }}
        showLabels
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 5,
          backgroundColor: '#edf2eaff',
          color: '#fff',
        }}
      >
        <BottomNavigationAction label="الرئيسية" value="home" icon={<Home />} />
        <BottomNavigationAction label="الحساب" value="account" icon={<AccountCircle />} />
        <BottomNavigationAction label="المحفظة" value="wallet" icon={<Wallet />} />
        {user?.role === 'admin' ? (
          <BottomNavigationAction label="لوحة التحكم" value="admin" icon={<AdminPanelSettings />} />
        ) : (
          <BottomNavigationAction label="إتصل بنا" value="about" icon={<PhoneIcon />} />
        )}
      </BottomNavigation>

      {/* Snackbar for status updates */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NavBar;